import { useCallback, useMemo, useRef, useState } from 'react';

/**
 * Multi-select for a list of rows: click toggles one row, shift-click adds the
 * contiguous range from the last clicked row. Lifted from the inbox
 * (InboxPage.tsx), which is the behaviour the rest of the app should match, so
 * the semantics below are the inbox's, not a redesign:
 *
 *  - Selection is keyed on ids, never indices, so it survives a refetch that
 *    reorders rows.
 *  - Shift-range is add-only. It never deselects.
 *  - Ctrl/cmd-click is the same as a plain click: it toggles one row and never
 *    clears the others.
 *  - Select-all acts on the visible ids only. Ids selected under a different
 *    filter or page stay selected. A caller that wants selection cleared on a
 *    page or filter change calls `clear` itself; the hook never clears on its
 *    own.
 */

/** Pure core of a selection click. Returns the next selected set. */
export function applySelectionClick<Id>(
  prev: ReadonlySet<Id>,
  orderedIds: readonly Id[],
  anchorId: Id | null,
  clickedId: Id,
  shiftKey: boolean
): Set<Id> {
  const next = new Set(prev);
  const clickedIndex = orderedIds.indexOf(clickedId);
  const anchorIndex = anchorId === null ? -1 : orderedIds.indexOf(anchorId);

  if (shiftKey && anchorIndex !== -1 && clickedIndex !== -1) {
    const start = Math.min(anchorIndex, clickedIndex);
    const end = Math.max(anchorIndex, clickedIndex);
    for (let i = start; i <= end; i++) next.add(orderedIds[i]);
    return next;
  }

  // Plain click, ctrl/cmd-click, and a shift-click with no usable anchor all
  // toggle the one row.
  if (next.has(clickedId)) next.delete(clickedId);
  else next.add(clickedId);
  return next;
}

/** Pure core of select-all: clears the visible ids if all are selected, else adds them. */
export function applyToggleAll<Id>(prev: ReadonlySet<Id>, visibleIds: readonly Id[]): Set<Id> {
  const next = new Set(prev);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
  if (allSelected) visibleIds.forEach((id) => next.delete(id));
  else visibleIds.forEach((id) => next.add(id));
  return next;
}

export interface RowSelection<Id> {
  selectedIds: ReadonlySet<Id>;
  isSelected: (id: Id) => boolean;
  /** True when there is at least one visible row and every visible row is selected. */
  allSelected: boolean;
  /** Handle a click on a row or its checkbox. Pass the event's shiftKey. */
  handleClick: (id: Id, modifiers?: { shiftKey?: boolean }) => void;
  toggleAll: () => void;
  /** Replace the selection with exactly one row (right-click on an unselected row). */
  selectOnly: (id: Id) => void;
  /**
   * Empty the selection. The shift anchor is reset too unless `keepAnchor` is
   * set. The inbox keeps it after a bulk action, so a shift-click right after
   * one still ranges from the last clicked row; that is its long-standing
   * behaviour and the refactor onto this hook preserves it.
   */
  clear: (options?: { keepAnchor?: boolean }) => void;
}

/**
 * @param orderedIds ids of the rows currently on screen, in display order.
 */
export function useRowSelection<Id>(orderedIds: readonly Id[]): RowSelection<Id> {
  const [selectedIds, setSelectedIds] = useState<Set<Id>>(() => new Set());
  const anchorRef = useRef<Id | null>(null);

  // Handlers read the latest order through a ref so their identity stays stable
  // while the list refetches underneath them.
  const orderedIdsRef = useRef(orderedIds);
  orderedIdsRef.current = orderedIds;

  const handleClick = useCallback((id: Id, modifiers?: { shiftKey?: boolean }) => {
    const anchorId = anchorRef.current;
    setSelectedIds((prev) =>
      applySelectionClick(prev, orderedIdsRef.current, anchorId, id, Boolean(modifiers?.shiftKey))
    );
    anchorRef.current = id;
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => applyToggleAll(prev, orderedIdsRef.current));
  }, []);

  const selectOnly = useCallback((id: Id) => {
    setSelectedIds(new Set([id]));
    anchorRef.current = id;
  }, []);

  const clear = useCallback((options?: { keepAnchor?: boolean }) => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
    if (!options?.keepAnchor) anchorRef.current = null;
  }, []);

  const isSelected = useCallback((id: Id) => selectedIds.has(id), [selectedIds]);

  const allSelected = useMemo(
    () => orderedIds.length > 0 && orderedIds.every((id) => selectedIds.has(id)),
    [orderedIds, selectedIds]
  );

  return { selectedIds, isSelected, allSelected, handleClick, toggleAll, selectOnly, clear };
}
